import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { ArrowBack, Delete as DeleteIcon } from '@mui/icons-material';
import { customerPaymentService } from '../services/customerPaymentService';
import { formatCurrency } from '../components/CurrencyTextField';
import { format } from 'date-fns';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) loadPayment();
  }, [id]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await customerPaymentService.getById(id);
      setPayment(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => (d ? format(new Date(d), 'MM/dd/yyyy') : '—');
  const customerName = payment?.company_name ||
    [payment?.customer_first_name, payment?.customer_last_name].filter(Boolean).join(' ') || '—';

  const orderDetailId = payment
    ? payment.resolved_work_order_id ?? payment.work_order_id
    : null;
  const orderDetailLabel = payment
    ? payment.resolved_work_order_label ||
      payment.work_order_identifier ||
      payment.work_order_number ||
      (orderDetailId != null ? `#${orderDetailId}` : null)
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!payment) {
    return (
      <Box>
        <Alert severity="error">Payment not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer-payments')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const totalAllocated = (payment.allocations || []).reduce((s, a) => s + parseFloat(a.amount_applied || 0), 0);

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      await customerPaymentService.delete(payment.id);
      setDeleteDialogOpen(false);
      navigate('/customer-payments');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete payment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/customer-payments')} title="Back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600, flex: 1 }}>
          Payment {payment.payment_number}
        </Typography>
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete payment
        </Button>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {payment.payment_number} ({formatCurrency(payment.amount)}) will be permanently removed.
            {(payment.allocations || []).length > 0
              ? ' Invoice allocations will be reversed and invoice balances updated.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Customer</Typography>
              <Typography variant="body2">
                {payment.customer_id ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/customers/${payment.customer_id}`)}
                  >
                    {customerName}
                  </Button>
                ) : (
                  customerName
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Order</Typography>
              <Typography variant="body2">
                {orderDetailId != null ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/work-orders/${orderDetailId}`)}
                  >
                    {orderDetailLabel || `Order #${orderDetailId}`}
                  </Button>
                ) : (
                  '—'
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2">{formatDate(payment.payment_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography variant="body2">{formatCurrency(payment.amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Method</Typography>
              <Typography variant="body2">{payment.payment_method || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Reference</Typography>
              <Typography variant="body2">{payment.reference_number || '—'}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Applied to Invoices</Typography>
          {(payment.allocations || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No allocations</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount Applied</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payment.allocations || []).map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/customer-invoices/${a.customer_invoice_id}`)}
                        >
                          {a.invoice_identifier || a.identifier || a.invoice_number || `#${a.customer_invoice_id}`}
                        </Button>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(a.amount_applied)}</TableCell>
                      <TableCell align="right">—</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2">
              Total applied: {formatCurrency(totalAllocated)} / {formatCurrency(payment.amount)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentDetail;
